"use client";

import React, { useState, useEffect } from "react";
import { Menu, Phone, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription, SheetClose } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu>
      <NavigationMenuList className="gap-1">
        {menuItems.filter(item => item.label !== "Action Footage").map((item, index) => <NavigationMenuItem key={item.id} data-index={index}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={cn(navigationMenuTriggerStyle(), "bg-transparent text-foreground hover:bg-primary/10 hover:text-primary focus:bg-primary/10 focus:text-primary uppercase font-bold tracking-wider text-sm transition-colors")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-3/4 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6 mt-12">
      {menuItems.filter(item => item.label !== "Action Footage").map((item, index) => <SheetClose asChild key={item.id} data-index={index}>
          <Link to={item.href || "#"} className="text-2xl font-bold uppercase tracking-wider text-foreground hover:text-primary transition-colors">
            {item.label}
          </Link>
        </SheetClose>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-transparent dark bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-xl border-border/40 shadow-lg py-2" : "bg-background py-4")}>
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
        {}
        <Link to="/" className="flex-shrink-0 z-50 relative">
          <img src="https://picked-bream.10web.cloud/wp-content/uploads/2026/03/Shark-bite-logo-Black.png" alt="Shark Bite Land Clearing" className="object-contain !w-[200px] !h-[120px]" />
        </Link>

        {}
        <div className="hidden lg:flex items-center gap-8">
          <MenuProvider menu_id="16" className="hidden lg:block">
            <DesktopMenuConsumer />
          </MenuProvider>

          <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-wide shadow-md hover:shadow-primary/20 transition-all">
            <Link to="tel:9034904941">
              <Phone className="mr-2 h-4 w-4" />
              Call Now
            </Link>
          </Button>
        </div>

        {}
        <div className="lg:hidden flex items-center gap-4">
          <Button asChild size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-wide text-xs">
            <Link to="tel:9034904941">
              <Phone className="mr-2 h-3 w-3" />
              Call
            </Link>
          </Button>

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent hover:text-accent-foreground">
                <Menu className="h-8 w-8" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md bg-background border-l border-border p-6 flex flex-col">
              <SheetHeader className="text-left border-b border-border pb-6">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <SheetDescription className="sr-only">
                  Main navigation for Shark Bite Land Clearing
                </SheetDescription>
                <Link to="/" className="inline-block">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569013/img/shark_bite_logo.png" alt="Shark Bite Land Clearing" className="h-10 w-auto" />
                </Link>
              </SheetHeader>

              <MenuProvider menu_id="16" className="flex-1">
                <MobileMenuConsumer />
              </MenuProvider>

              <div className="mt-auto pt-8 border-t border-border">
                <Button asChild className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-wide py-6 text-lg">
                  <Link to="tel:9034904941">
                    <Phone className="mr-2 h-5 w-5" />
                    Call Now: 903 490 4941
                  </Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>;
}